import { css } from 'aphrodite';
import {
	Dialog,
	DialogPopup,
	DialogHeader,
	DialogTitle,
	DialogBody,
	DialogFooter,
} from '../../../components-v2/Dialog';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { type ValidationResult } from '../utils/validateAutomation';
import { styleSheet } from './styles/PublishDialogStyles';

interface PublishDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	isPublishing: boolean;
	validationResult: ValidationResult | null;
	automationName: string;
}

export function PublishDialog({
	isOpen,
	onClose,
	onConfirm,
	isPublishing,
	validationResult,
	automationName,
}: PublishDialogProps) {
	const hasErrors = validationResult && !validationResult.isValid;
	const hasWarnings = validationResult && validationResult.warnings.length > 0;

	return (
		<Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>{hasErrors ? 'Cannot Publish Automation' : 'Publish Automation'}</DialogTitle>
				</DialogHeader>

				<DialogBody>
					{hasErrors ? (
						<div>
							<p className={css(styleSheet.description)}>The following issues must be resolved before publishing:</p>
							<div className={css(styleSheet.errorList)}>
								{validationResult.errors.map((error, index) => (
									<div key={index} className={css(styleSheet.errorItem)}>
										<svg
											width='16'
											height='16'
											viewBox='0 0 16 16'
											fill='none'
											className={css(styleSheet.errorIcon)}
											aria-hidden='true'
										>
											<circle cx='8' cy='8' r='7' fill={THEME.colors.danger.main} />
											<path d='M8 5v4M8 11v1' stroke='white' strokeWidth='2' strokeLinecap='round' />
										</svg>
										<span>{error.message}</span>
									</div>
								))}
							</div>
						</div>
					) : (
						<div>
							<p className={css(styleSheet.description)}>
								You are about to publish <strong>{automationName}</strong>. This will make the automation active and
								start processing contacts based on the trigger.
							</p>

							{hasWarnings && (
								<div className={css(styleSheet.warningSection)}>
									<h3 className={css(styleSheet.warningTitle)}>Warnings:</h3>
									<div className={css(styleSheet.warningList)}>
										{validationResult.warnings.map((warning, index) => (
											<div key={index} className={css(styleSheet.warningItem)}>
												<svg
													width='16'
													height='16'
													viewBox='0 0 16 16'
													fill='none'
													className={css(styleSheet.warningIcon)}
													aria-hidden='true'
												>
													<path
														d='M8 1l7 12H1L8 1z'
														fill={THEME.colors.warning.main}
														stroke={THEME.colors.warning.main}
														strokeWidth='1'
														strokeLinejoin='round'
													/>
													<path d='M8 6v3M8 11v1' stroke='white' strokeWidth='1.5' strokeLinecap='round' />
												</svg>
												<span>{warning.message}</span>
											</div>
										))}
									</div>
								</div>
							)}

							<p className={css(styleSheet.confirmText)}>Are you sure you want to publish this automation?</p>
						</div>
					)}
				</DialogBody>

				<DialogFooter styles={[styleSheet.actions]}>
					<Button variant='outline' onClick={onClose} disabled={isPublishing}>
						{hasErrors ? 'Close' : 'Cancel'}
					</Button>
					{!hasErrors && (
						<Button variant='primary' onClick={onConfirm} isLoading={isPublishing}>
							Publish
						</Button>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
